import styled from '@emotion/styled';
import React, { useEffect, useRef } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
// @ts-ignore
import { windowUndefined } from '../../../../../helpers/serverRenderingUtils';
import {
  AnalyticsIcon,
  HostingIcon,
  MonetizationIcon,
  RecordIcon,
} from './SubNavigationSvgs';

const smoothScroolToSection = (
  e: React.MouseEvent<HTMLAnchorElement>,
  id: string
) => {
  e.preventDefault();
  if (!windowUndefined()) {
    const el = document.getElementById(id) as HTMLElement;
    el.scrollIntoView({
      behavior: 'smooth',
    });
  }
};

export function SubNavigation({ activeItem }: { activeItem: string }) {
  const progressEl = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const { scrollHeight, clientHeight, scrollTop } = document.documentElement;
    const scrollPos = (scrollTop / (scrollHeight - clientHeight)) * 100;

    if (progressEl && progressEl.current) {
      progressEl.current.style.setProperty('--scroll', `${scrollPos}%`);
    }
  };

  // only show scroll progress bar and attach event listeners if browser supports css variables
  const showScrollProgress = windowUndefined()
    ? false
    : window.CSS && CSS.supports('--foo: red');

  useEffect(() => {
    // polyfill native scrollIntoView for iOS
    smoothscroll.polyfill();
  }, []);

  useEffect(() => {
    if (showScrollProgress) {
      document.addEventListener('scroll', handleScroll);
    }
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [showScrollProgress]);

  return (
    <NavContainer>
      <Nav>
        <li>
          <NavItem
            href="creation-tools"
            onClick={e => smoothScroolToSection(e, 'creation-tools')}
            isActive={activeItem === 'creation-tools'}
            aria-current={activeItem === 'creation-tools' ? 'page' : undefined}
          >
            <RecordIcon />
            <span>Easy-to-use creation tools</span>
          </NavItem>
        </li>
        <li>
          <NavItem
            href="hosting"
            onClick={e => smoothScroolToSection(e, 'hosting')}
            isActive={activeItem === 'hosting'}
            aria-current={activeItem === 'hosting' ? 'page' : undefined}
          >
            <HostingIcon />
            <span>Free hosting &amp; distribution</span>
          </NavItem>
        </li>
        <li>
          <NavItem
            href="analytics"
            onClick={e => smoothScroolToSection(e, 'analytics')}
            isActive={activeItem === 'analytics'}
            aria-current={activeItem === 'analytics' ? 'page' : undefined}
          >
            <AnalyticsIcon />
            <span>Straightforward analytics</span>
          </NavItem>
        </li>
        <li>
          <NavItem
            href="sponsorships"
            onClick={e => smoothScroolToSection(e, 'sponsorships')}
            isActive={activeItem === 'sponsorships'}
            aria-current={activeItem === 'sponsorships' ? 'page' : undefined}
          >
            <MonetizationIcon />
            <span>Get paid to podcast</span>
          </NavItem>
        </li>
      </Nav>
      {showScrollProgress && <ScrollProgress ref={progressEl} />}
    </NavContainer>
  );
}

const NavContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  position: fixed;
  top: 96px;
  z-index: 1;
  background: #ffffff;
  display: flex;
  justify-content: center;
  height: 50px;

  @media (max-width: 880px) {
    top: 76px;
  }
`;
const Nav = styled.ul`
  padding: 0 20px;
  width: 100%;
  margin: 0;
  list-style: none;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 0 26px;
  max-width: 1400px;
`;

const ScrollProgress = styled.div`
  position: fixed;
  width: 100%;
  height: 2px;
  top: 144px;
  z-index: 1;
  @supports (--foo: red) {
    background: linear-gradient(to right, #8940fa var(--scroll), transparent 0);
    background-repeat: no-repeat;
  }
  @media (max-width: 880px) {
    top: 124px;
  }
`;

type NavItemProps = {
  isActive: boolean;
};

const NavItem = styled.a<NavItemProps>`
  font-size: 1.4rem;
  font-weight: bold;
  color: ${({ isActive }: NavItemProps) => (isActive ? '#292f36' : '#c9cbcd')};
  path {
    fill: ${({ isActive }: NavItemProps) => (isActive ? '#292f36' : '#c9cbcd')};
  }
  &:hover {
    path {
      fill: #292f36;
    }
    text-decoration: none;
    color: #292f36;
  }
  &:active,
  &:focus,
  &:active:focus {
    text-decoration: none;
    color: ${({ isActive }: NavItemProps) =>
      isActive ? '#292f36' : '#c9cbcd'};
    path {
      fill: ${({ isActive }: NavItemProps) =>
        isActive ? '#292f36' : '#c9cbcd'};
    }
  }
  span {
    clip: rect(1px, 1px, 1px, 1px);
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    height: 1px;
    width: 1px;
  }
  @media (min-width: 880px) {
    display: flex;
    align-items: center;
    span {
      position: initial;
      display: inline-block;
      padding-left: 12px;
      clip: initial;
      width: initial;
      height: initial;
    }
  }
`;
